import {
  Box,
  Button,
  Divider,
  Group,
  Menu,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {
  DefaultValues,
  FieldPath,
  SubmitHandler,
  useFormContext,
} from 'react-hook-form';
import {Link, useNavigate} from 'react-router-dom';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {useAsync, useAsyncFn} from 'react-use';
import {ClientNamesService} from '../api/generated/ClientNamesService.ts';
import {CodesService} from '../api/generated/CodesService.ts';
import {JobsService} from '../api/generated/JobsService';
import {PartnerSitesService} from '../api/generated/PartnerSitesService.ts';
import {UsersService} from '../api/generated/UsersService.ts';
import {CodeClassTypes, JobTypes, Roles} from '../api/generated/enums.ts';
import {useRequireAuthorization} from '../auth/require-authorization.tsx';
import {AsyncStateContainer} from '../components/async-state-container.tsx';
import {BasicPage} from '../components/basic-page';
import {DeleteButton} from '../components/delete-button.tsx';
import {BreadcrumbPart} from '../components/page-title.tsx';
import {createForm} from '../forms';
import {useNotification} from '../hooks/use-notifications.tsx';
import {ROUTES} from '../routes';
import {buildPath} from '../routes/utils.ts';
import {JobDetails} from './job-details.tsx';
import {useJobStore} from './job-store.tsx';
import {
  BaseJobSchema,
  mmJobSchema,
  MmJobSchema,
  nonMmJobSchema,
} from './schemas.ts';
import {useDebouncedState} from '../hooks/use-debounced-state.ts';
import {useFieldWatch} from '../forms/use-field-watch.ts';
import {QuickAddServiceCall} from '../components/inputs/combobox/types.ts';
import {selectNumberConverter} from '../forms/value-converters.ts';
import {useUser} from '../auth/use-auth.tsx';

const breadcrumbs: BreadcrumbPart[] = [
  {title: 'Jobs', href: ROUTES.ADMIN.JOBS.INDEX.path},
  {title: 'Create'},
];

// Helper function to convert null values to undefined for form compatibility
// and parse date strings to Date objects
const mapJobNullsToUndefined = (obj: any): any => {
  if (obj === null) return undefined;
  if (obj === undefined) return undefined;

  // Check if it's a date string and convert to Date object
  if (
    typeof obj === 'string' &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(obj)
  ) {
    const date = new Date(obj);
    // Return undefined for invalid dates (like 0001-01-01)
    return date.getFullYear() <= 1 ? undefined : date;
  }

  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map(mapJobNullsToUndefined);
  }

  return Object.entries(obj).reduce((acc, [key, value]) => {
    acc[key] = mapJobNullsToUndefined(value);
    return acc;
  }, {} as any);
};

const Form = createForm<BaseJobSchema>();

export const JobCreate = () => {
  const navigate = useNavigate();
  const notifications = useNotification();
  const {jobType} = useTypedParams(ROUTES.ADMIN.JOBS.CREATE);

  const _defaultValues = useMemo<DefaultValues<BaseJobSchema>>(
    () => ({jobType}),
    [jobType],
  );

  const onSubmit = useCallback<SubmitHandler<BaseJobSchema>>(
    async (value) => {
      const response = await JobsService.create({body: value});

      if (response.hasErrors) {
        response.errors.forEach((item) =>
          notifications.error(item.errorMessage),
        );
        return response;
      }

      notifications.success('Job Created');

      if (response.data && response.data.id) {
        navigate(
          buildPath(ROUTES.ADMIN.JOBS.DETAIL.path, {
            id: response.data.id,
          }),
        );
      }
    },
    [navigate, notifications],
  );

  const schema = useSchema(jobType);

  return (
    <BasicPage title={breadcrumbs}>
      <Form defaultValues={_defaultValues} schema={schema} onSubmit={onSubmit}>
        {({formState: {isSubmitting}}) => (
          <Form.Container>
            {jobType === JobTypes['M&M'] ? (
              <MmJobFormFields isCreate />
            ) : (
              <NonMmJobFormFields isCreate />
            )}
            <Group mt={24} justify={'flex-end'}>
              <Button
                type='button'
                variant='outline'
                component={Link}
                to={ROUTES.ADMIN.JOBS.INDEX.path}
              >
                Cancel
              </Button>
              <Form.Submit loading={isSubmitting}>Create Job</Form.Submit>
            </Group>
          </Form.Container>
        )}
      </Form>
    </BasicPage>
  );
};

export const JobUpdate: FC = () => {
  const navigate = useNavigate();
  const notifications = useNotification();
  const {id} = useTypedParams(ROUTES.ADMIN.JOBS.DETAIL);
  const user = useUser();
  const currentUserId = user.id;

  // Strict role checks so Global Admin does not satisfy other role checks implicitly
  const isGlobalAdmin = useRequireAuthorization({
    role: Roles['Global Admin'],
    strict: true,
  });
  const isPartnerSiteManager = useRequireAuthorization({
    role: Roles['Partner Site Manager'],
    strict: true,
  });
  const isPartnerSiteUser = useRequireAuthorization({
    role: Roles['Partner Site User'],
    strict: true,
  });

  const {job, isLoading, isJobReadonly, fetchJob, clearJob} = useJobStore(
    (state) => ({
      job: state.job ?? undefined,
      isLoading: state.isJobLoading,
      isJobReadonly: state.isJobReadonly,
      fetchJob: state.fetchJob,
      clearJob: state.clearJob,
    }),
  );

  useEffect(() => {
    return () => clearJob();
  }, [clearJob]);

  const {value: userPartnerSiteIdsResponse} = useAsync(
    async () => await UsersService.getPartnerSiteIds(),
    [],
  );

  const userPartnerSiteIds = userPartnerSiteIdsResponse?.data ?? [];
  const hasSiteAccess =
    job?.partnerSiteId != null
      ? userPartnerSiteIds.some((x) => x === job.partnerSiteId)
      : false;

  const isOwner =
    job?.createdByUserId != null && currentUserId === job.createdByUserId;

  const shouldRenderDelete = useMemo(() => {
    // Align with backend delete rules and hide for archived jobs
    const allowedByRole =
      isGlobalAdmin ||
      (isPartnerSiteManager && hasSiteAccess) ||
      (isPartnerSiteUser && hasSiteAccess && isOwner);

    return !isJobReadonly && allowedByRole;
  }, [
    isGlobalAdmin,
    isPartnerSiteManager,
    isPartnerSiteUser,
    hasSiteAccess,
    isOwner,
    isJobReadonly,
  ]);

  const onSubmit = useCallback<SubmitHandler<BaseJobSchema>>(
    async (value) => {
      const response = await JobsService.update({
        id,
        body: {
          ...value,
          status: job?.status,
        },
      });

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Job Updated');
      await fetchJob(id);
    },
    [fetchJob, id, job, notifications],
  );

  const [deleteJobState, deleteJob] = useAsyncFn(async () => {
    const response = await JobsService.deleteById({id});

    if (response.hasErrors) {
      notifications.error('Unable to delete job');
      return;
    }

    notifications.success('Job deleted');
    navigate(ROUTES.ADMIN.JOBS.INDEX.path);
  }, [id, navigate, notifications]);

  const schema = useSchema(job?.jobType as JobTypes);

  return (
    <BasicPage p={0} maxWidth='97%'>
      <AsyncStateContainer loading={!job && isLoading}>
        <Stack gap='xl'>
          <Paper withBorder shadow='sm'>
            <JobDetails job={job} />
            <Divider />
            <Form
              schema={schema}
              onSubmit={onSubmit}
              defaultValues={job ? mapJobNullsToUndefined(job) : undefined}
            >
              {({formState: {isSubmitting}}) => (
                <Form.Container withBorder={false}>
                  {job?.jobType === JobTypes['M&M'] ? (
                    <MmJobFormFields isReadonly={isJobReadonly} />
                  ) : (
                    <NonMmJobFormFields isReadonly={isJobReadonly} />
                  )}
                  <Group mt={24} justify='space-between'>
                    <div>
                      {shouldRenderDelete && (
                        <DeleteButton
                          onConfirm={deleteJob}
                          buttonLabel='Delete Job'
                          confirmButtonLabel='Delete Job'
                          loading={deleteJobState.loading}
                          confirmTitle={
                            <Text fw={600}>
                              Are you sure you want to delete this job?
                            </Text>
                          }
                        />
                      )}
                    </div>
                    <Group>
                      <Button
                        type='button'
                        variant='outline'
                        component={Link}
                        to={ROUTES.ADMIN.JOBS.INDEX.path}
                      >
                        Cancel
                      </Button>
                      <Form.Submit
                        loading={isSubmitting}
                        disabled={isJobReadonly}
                      >
                        Update Job
                      </Form.Submit>
                    </Group>
                  </Group>
                </Form.Container>
              )}
            </Form>
          </Paper>
        </Stack>
      </AsyncStateContainer>
    </BasicPage>
  );
};

const useSchema = (jobType: JobTypes) => {
  return jobType === JobTypes['M&M'] ? mmJobSchema : nonMmJobSchema;
};

type FormFieldsProps = {
  isCreate?: boolean;
  isReadonly?: boolean;
  isSelectablePartnerSiteAuthorized?: boolean;
};

const MmJobFormFields: FC<FormFieldsProps> = ({isCreate, isReadonly}) => {
  return (
    <Stack gap='xs'>
      <Form.Row>
        {isCreate && (
          <Box w='24%' flex='none'>
            <Form.TextInput
              name='serialNumber'
              label='Serial Number'
              placeholder='Serial Number'
            />
          </Box>
        )}
        <Box w='24%' flex='none'>
          <CodeSelectField
            isReadonly={isReadonly}
            codeClass={CodeClassTypes['Level of Service']}
            name={'levelOfServiceId'}
            label={'Level of Service'}
          />
        </Box>
        {isCreate && (
          <Form.Row>
            <PartnerSitesField />
          </Form.Row>
        )}
      </Form.Row>
      <Form.Row>
        <ClientNameField isReadonly={isReadonly} />
        <Form.TextInput
          name='assetNumber'
          label='Asset Number'
          placeholder='Asset Number'
          disabled={isReadonly}
        />
        <Form.TextInput
          name='reference1'
          label='Reference 1'
          placeholder='Reference 1'
          disabled={isReadonly}
        />
        <Form.TextInput
          name='reference2'
          label='Reference 2'
          placeholder='Reference 2'
          disabled={isReadonly}
        />
      </Form.Row>
      <Form.Row>
        <Form.TextArea
          name='memo'
          label='Memo'
          placeholder='Memo'
          description='Prints on certs'
          disabled={isReadonly}
        />
        <Form.TextArea
          name='notes'
          label='Notes'
          placeholder='Notes'
          description='Internal only'
          disabled={isReadonly}
        />
      </Form.Row>
    </Stack>
  );
};

const NonMmJobFormFields: FC<FormFieldsProps> = ({isCreate, isReadonly}) => {
  return (
    <Stack gap='xs'>
      <Form.Row>
        {isCreate && (
          <Form.TextInput
            name='serialNumber'
            label='Serial Number'
            placeholder='Serial Number'
          />
        )}
        {isCreate && <PartnerSitesField />}
        <ClientNameField isReadonly={isReadonly} />
      </Form.Row>

      <Form.Row>
        <CodeSelectField
          isReadonly={isReadonly}
          codeClass={CodeClassTypes.OEM}
          name={'oemCodeId'}
          label={'OEM'}
        />
        <CodeSelectField
          isReadonly={isReadonly}
          codeClass={CodeClassTypes['Part Type']}
          name={'partTypeCodeId'}
          label={'Part Type'}
        />
      </Form.Row>

      <Form.Row>
        <Form.TextInput
          name='assetNumber'
          label='Asset Number'
          placeholder='Asset Number'
          disabled={isReadonly}
        />
        <Form.TextInput
          name='partNumber'
          label='Part Number'
          placeholder='Part Number'
          disabled={isReadonly}
        />
        <Form.TextInput
          name='partDescription'
          label='Part Description'
          placeholder='Part Description'
          disabled={isReadonly}
        />
      </Form.Row>

      <Form.Row>
        <CodeSelectField
          isReadonly={isReadonly}
          codeClass={CodeClassTypes['Upper Connection']}
          name={'upperConnectionCodeId'}
          label={'Upper Connection'}
        />
        <CodeSelectField
          isReadonly={isReadonly}
          codeClass={CodeClassTypes['Middle Connection']}
          name={'middleConnectionCodeId'}
          label={'Middle Connection'}
        />
        <CodeSelectField
          isReadonly={isReadonly}
          codeClass={CodeClassTypes['Lower Connection']}
          name={'lowerConnectionCodeId'}
          label={'Lower Connection'}
        />
      </Form.Row>

      <Form.Row>
        <CodeSelectField
          isReadonly={isReadonly}
          codeClass={CodeClassTypes['Working Pressure']}
          name={'workingPressureCodeId'}
          label={'Working Pressure'}
        />
        <CodeSelectField
          isReadonly={isReadonly}
          codeClass={CodeClassTypes['Service']}
          name={'serviceCodeId'}
          label={'Service'}
        />
        <Form.NumberInput
          readOnly={isReadonly}
          name={'currentShShLength'}
          label={'Current SH-SH Length'}
          min={0}
          suffix={' inches'}
          placeholder={'Current SH-SH Length'}
        />
      </Form.Row>

      <Form.Row>
        <Form.TextInput
          name='reference1'
          label='Reference 1'
          placeholder='Reference 1'
          disabled={isReadonly}
        />
        <Form.TextInput
          name='reference2'
          label='Reference 2'
          placeholder='Reference 2'
          disabled={isReadonly}
        />
      </Form.Row>

      <Form.Row>
        <Form.TextArea
          name='memo'
          label='Memo'
          placeholder='Memo'
          description='Prints on certs'
          disabled={isReadonly}
        />
        <Form.TextArea
          name='notes'
          label='Notes'
          placeholder='Notes'
          description='Internal only'
          disabled={isReadonly}
        />
      </Form.Row>
    </Stack>
  );
};

const PartnerSitesField = () => {
  const fetchPartnerSiteOptions = useAsync(async () => {
    return await PartnerSitesService.getOptions();
  }, []);
  const partnerSiteOptions = fetchPartnerSiteOptions.value?.data ?? [];

  return (
    <Form.Select
      searchable
      loading={fetchPartnerSiteOptions.loading}
      name='partnerSiteId'
      label='Partner Site'
      data={partnerSiteOptions}
    />
  );
};

type CommonFieldProps = {
  isReadonly?: boolean;
};
const ClientNameField: FC<CommonFieldProps> = (props) => {
  const {isReadonly} = props;
  const [clientNameFocused, setClientNameFocused] = useState(false);
  const [clientNameDebouncedState, setClientNameDebouncedState] =
    useDebouncedState('', 500);
  const {control, setValue} = useFormContext<MmJobSchema>();

  useFieldWatch(control, 'clientName', (value) =>
    setClientNameDebouncedState(value ?? ''),
  );

  const handleClientNameSelect = useCallback(
    (item: string | undefined) => {
      setValue('clientName', String(item));
    },
    [setValue],
  );

  const fetchClientNameOptions = useAsync(async () => {
    if (!clientNameDebouncedState) return;

    return await ClientNamesService.getOptions({
      value: clientNameDebouncedState,
    });
  }, [clientNameDebouncedState]);
  const clientNameOptions = fetchClientNameOptions.value?.data ?? [];

  return (
    <Menu opened={clientNameFocused} trapFocus={false} returnFocus={false}>
      <Menu.Target>
        <div>
          <Form.TextInput
            name='clientName'
            label='Client Name'
            placeholder='Client Name'
            disabled={isReadonly}
            onFocus={() => setClientNameFocused(true)}
            onBlur={() => setClientNameFocused(false)}
          />
        </div>
      </Menu.Target>
      <Menu.Dropdown w={300}>
        {clientNameOptions.map((item) => (
          <Menu.Item onClick={() => handleClientNameSelect(item.label)}>
            {item.label ?? ''}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
};

type CodeSelectFieldProps<
  TName extends FieldPath<MmJobSchema> = FieldPath<MmJobSchema>,
> = {
  codeClass: CodeClassTypes;
  name: TName;
  label: string;
  placeholder?: string;
  isReadonly?: boolean;
};
const CodeSelectField: FC<CodeSelectFieldProps> = (props) => {
  const {codeClass, name, isReadonly, label, placeholder = label} = props;
  const {value, loading} = useAsync(async () => {
    return await CodesService.getOptionsByClass({
      codeClass: codeClass,
    });
  }, [codeClass]);
  const options = value?.data ?? [];

  return (
    <Form.Select
      searchable
      creatable
      loading={loading}
      name={name}
      data={options}
      clearable={!isReadonly}
      disabled={isReadonly}
      label={label}
      placeholder={placeholder}
      onCreate={onCodeCreate(codeClass)}
      transformer={selectNumberConverter}
    />
  );
};

const onCodeCreate =
  (type: CodeClassTypes): QuickAddServiceCall<unknown> =>
  async (description) => {
    return await CodesService.quickAdd({
      body: {
        description,
        codeClass: type,
        inactive: false,
        sortOrder: 0,
      },
    });
  };